/**
 * Parallax background for TeaserTextBox (.teaser-textbox__image-bg).
 *
 * The background layer is oversized (130% height, see TeaserTextBox.scss)
 * and translated vertically based on how far the module has scrolled
 * through the viewport, so it moves slightly slower than the page — the
 * classic parallax feel. Same scroll-linked rAF-throttled pattern as
 * GradientScroll.js.
 *
 * @return {void}
 */
export default function InitTeaserTextBox() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        return
    }

    // this is a purely visual frontend effect — never run it in the
    // backend, where editors need to see the image in its resting position
    if (window.neos || document.body.classList.contains('neos-backend')) {
        return
    }

    const layers = Array.from(document.querySelectorAll('[data-teaser-parallax-bg]'))
    if (!layers.length) {
        return
    }

    const RANGE = 130 // max px of vertical travel in either direction

    let ticking = false

    const update = () => {
        const vh = window.innerHeight

        layers.forEach((bg) => {
            const container = bg.closest('[data-teaser-parallax]')
            if (!container) {
                return
            }

            const rect = container.getBoundingClientRect()
            // 0 when the module's center is at the viewport's center, -1/+1
            // a half-viewport-height's worth of scrolling below/above that —
            // deliberately NOT factoring in the module's own height here
            // (unlike a more "textbook" progress formula): for a module
            // noticeably shorter than the viewport, that would spread the
            // full ±RANGE motion over a much longer scroll distance than a
            // half viewport height, making the effect barely perceptible
            // during a normal scroll past it.
            const progress = (rect.top + rect.height / 2 - vh / 2) / (vh / 2)
            const clamped = Math.max(-1, Math.min(1, progress))
            bg.style.transform = `translateY(${(clamped * -RANGE).toFixed(1)}px)`
        })

        ticking = false
    }

    const requestUpdate = () => {
        if (!ticking) {
            ticking = true
            window.requestAnimationFrame(update)
        }
    }

    window.addEventListener('scroll', requestUpdate, { passive: true })
    window.addEventListener('resize', requestUpdate)
    update()
}
